import { Divider, Group, Select } from '@byecode/ui'
import type { IndependentAction } from '@lighthouse/core'
import type { DeleteRecordActionConfigure as DeleteRecordActionNode, FlowNode, NodeTypes, VariableTree2 } from '@lighthouse/shared'
import {
    generateJson,
    getAllCanBeUpstreamNodes,
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getInputVariableOption,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    OUTPUT_SINGLE_DATA_NODES,
    OUTPUT_SINGLE_RECORD_NODES,
    PAGE_LINK_OPTION,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import { availableFieldBlocksAtom, availableFilterBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../styles'
import { SelectRecordControlled } from './component/SelectRecordControlled'

interface DeleteRecordActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    value: DeleteRecordActionNode['config']
    onChange: (value: DeleteRecordActionNode['config']) => void
}

export const DeleteRecordActionConfigure: React.FC<DeleteRecordActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    allParentActions,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const upstreamNodesForVariableSelect = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )
    const upstreamNodesForSource = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_SINGLE_RECORD_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : getUpstreamActionsOptions({
                      workflowList,
                      allParentActions: allParentActions || [],
                      dataSourceList
                  }),
        [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, isInFlow, dataSourceList, handleMouseEnter, handleMouseLeave])

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []

        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }

        if (customViewOption) {
            options.push(customViewOption)
        }

        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const finalPageOptions = useMemo(() => [PAGE_LINK_OPTION, ...(pageOptions || [])], [pageOptions])

    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>

            <Divider />

            <Group label="选择记录">
                <SelectRecordControlled
                    value={value}
                    onChange={next => onChange(next)}
                    upstreamNodesForVariableSelect={upstreamNodesForVariableSelect}
                    upstreamNodesForSource={upstreamNodesForSource}
                    allParentActions={allParentActions}
                    upstreamRecordIsFilterAndSort={false}
                    disableUpstreamRecord={!isInFlow}
                />
            </Group>

            {isInFlow ? null : (
                <>
                    <Divider />

                    <Group
                        label="二次确认弹窗"
                        mode="switch"
                        defaultOpen={false}
                        opened={!!value?.confirm?.enable}
                        onCollapseChange={v => {
                            if (v) {
                                onChange({
                                    ...value,
                                    confirm: {
                                        enable: true,
                                        title: generateJson('确认删除？'),
                                        description: generateJson('删除后不可恢复，请谨慎操作。'),
                                        confirmText: generateJson('删除'),
                                        cancelText: generateJson('取消')
                                    }
                                })
                                return
                            }
                            onChange({
                                ...value,
                                confirm: {
                                    ...value?.confirm,
                                    enable: false
                                }
                            })
                        }}
                    >
                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>弹窗标题</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    autoHeight
                                    value={value?.confirm?.title}
                                    onBlur={next =>
                                        onChange({
                                            ...value,
                                            confirm: { ...value?.confirm, title: next }
                                        })
                                    }
                                    inputOption={inputOption}
                                    viewOptions={viewOptions}
                                    formOption={formOption}
                                    pageOptions={finalPageOptions}
                                    upstreamOptions={upstreamOptions}
                                    userOption={userOption}
                                    filterOption={filterOption}
                                    dataSourceList={dataSourceList}
                                    imageOption={imageOption}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>描述文案</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <NodeFieldPreview
                                    autoHeight
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    value={value?.confirm?.description}
                                    onBlur={next =>
                                        onChange({
                                            ...value,
                                            confirm: { ...value?.confirm, description: next }
                                        })
                                    }
                                    inputOption={inputOption}
                                    viewOptions={viewOptions}
                                    formOption={formOption}
                                    pageOptions={finalPageOptions}
                                    upstreamOptions={upstreamOptions}
                                    userOption={userOption}
                                    filterOption={filterOption}
                                    dataSourceList={dataSourceList}
                                    imageOption={imageOption}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>删除按钮文案</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <NodeFieldPreview
                                    autoHeight
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    value={value?.confirm?.confirmText}
                                    onBlur={next =>
                                        onChange({
                                            ...value,
                                            confirm: { ...value?.confirm, confirmText: next }
                                        })
                                    }
                                    inputOption={inputOption}
                                    viewOptions={viewOptions}
                                    formOption={formOption}
                                    pageOptions={finalPageOptions}
                                    upstreamOptions={upstreamOptions}
                                    userOption={userOption}
                                    filterOption={filterOption}
                                    dataSourceList={dataSourceList}
                                    imageOption={imageOption}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>

                        <ListItem4ByecodeUi>
                            <SC.FormItemLabelWrapper>
                                <SC.FormItemLabel>取消按钮文案</SC.FormItemLabel>
                            </SC.FormItemLabelWrapper>
                            <SC.FormItemContent>
                                <NodeFieldPreview
                                    autoHeight
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    value={value?.confirm?.cancelText}
                                    onBlur={next =>
                                        onChange({
                                            ...value,
                                            confirm: { ...value?.confirm, cancelText: next }
                                        })
                                    }
                                    inputOption={inputOption}
                                    viewOptions={viewOptions}
                                    formOption={formOption}
                                    pageOptions={finalPageOptions}
                                    upstreamOptions={upstreamOptions}
                                    userOption={userOption}
                                    filterOption={filterOption}
                                    dataSourceList={dataSourceList}
                                    imageOption={imageOption}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                </>
            )}
        </SC.Container>
    )
}
